/**
 * extras.ts — Categoría "Extras" (11 productos demo para restaurante).
 *
 * Bebidas, panadería, fruta, proteína/sopa/principio adicional y adiciones al pedido.
 * Modificadores en _modifiersExtras.ts; imágenes en _extrasImages.ts.
 */
import { buildProduct } from "./_factory";
import { EXTRAS_MODIFIERS_BY_INDEX } from "./_modifiersExtras";
import { EXTRAS_GALERIA_BY_INDEX } from "./_extrasImages";
import {
  HORARIO_EXTRAS,
  withDemoCajaLibreIfModifiers,
} from "./_restaurantDemo.constants";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_extra",
  skuPrefix: "EXTRA",
  categoria: "Extras",
} as const;

const extra = (
  input: Omit<Parameters<typeof buildProduct>[0], keyof typeof CAT>,
) =>
  buildProduct({
    ...CAT,
    disponibilidadHoraria: HORARIO_EXTRAS,
    ...withDemoCajaLibreIfModifiers(input),
  });

export const cat_extra_products: Producto[] = [
  extra({
    index: 0,
    nombre: "Café",
    marca: "Casa",
    costo: 0,
    precio: 3000,
    stock: 120,
    stockMinimo: 30,
    stockMaximo: 200,
    unidad: "taza",
    ubicacionFisica: "Barra caliente, cafetera",
    proveedor: "Producción propia",
    tiempoEntrega: "2 min",
    descripcionLarga:
      "Tinto o perico recién colado, servido caliente. Ideal para acompañar el almuerzo o cerrar la comida.",
    especificaciones: [
      { clave: "Tipo", valor: "Café colombiano" },
      { clave: "Precio", valor: "$3.000" },
    ],
    imageKeywords: ["coffee-cup", "colombian-coffee", "espresso"],
    color: "bg-amber-900",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Café Molido Juan Valdez 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 18500,
        cantidadUso: 12,
        unidadUso: "g",
      },
      {
        nombre: "Azúcar Blanca 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 3200,
        cantidadUso: 8,
        unidadUso: "g",
      },
    ],
    galeria: EXTRAS_GALERIA_BY_INDEX[0],
  }),
  extra({
    index: 1,
    nombre: "Chocolate",
    marca: "Casa",
    costo: 0,
    precio: 3000,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 150,
    unidad: "taza",
    ubicacionFisica: "Barra caliente, olla de chocolate",
    proveedor: "Producción propia",
    tiempoEntrega: "3 min",
    descripcionLarga:
      "Chocolate caliente espeso con leche entera. Bebida reconfortante para cualquier momento del día.",
    especificaciones: [
      { clave: "Bebida", valor: "Chocolate caliente" },
      { clave: "Precio", valor: "$3.000" },
    ],
    imageKeywords: ["hot-chocolate", "milk-drink", "cocoa"],
    color: "bg-amber-800",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Leche Entera Alquería 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 4200,
        cantidadUso: 200,
        unidadUso: "ml",
      },
      {
        nombre: "Chocolate Corona 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 8500,
        cantidadUso: 25,
        unidadUso: "g",
      },
    ],
    galeria: EXTRAS_GALERIA_BY_INDEX[1],
  }),
  extra({
    index: 2,
    nombre: "Pan",
    marca: "Casa",
    costo: 0,
    precio: 500,
    stock: 200,
    stockMinimo: 50,
    stockMaximo: 400,
    unidad: "unidad",
    ubicacionFisica: "Panadería, vitrina",
    proveedor: "Panadería del barrio",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Pan blanco tajado del día, suave y fresco. Porción individual para acompañar sopas, huevos o caldos.",
    especificaciones: [
      { clave: "Unidad", valor: "1 rebanada / panito" },
      { clave: "Precio", valor: "$500" },
    ],
    imageKeywords: ["bread-slice", "white-bread", "bakery"],
    color: "bg-amber-200",
    shape: "square",
    gastoMargen: "conservador",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[2],
  }),
  extra({
    index: 3,
    nombre: "Arepa",
    marca: "Casa",
    costo: 0,
    precio: 1000,
    stock: 90,
    stockMinimo: 25,
    stockMaximo: 180,
    unidad: "unidad",
    ubicacionFisica: "Plancha, zona desayunos",
    proveedor: "Producción propia",
    tiempoEntrega: "5 min",
    descripcionLarga:
      "Arepa de maíz dorada a la plancha, crujiente por fuera y suave por dentro. Acompañante clásico colombiano.",
    especificaciones: [
      { clave: "Tipo", valor: "Arepa de maíz" },
      { clave: "Precio", valor: "$1.000" },
    ],
    imageKeywords: ["arepa", "colombian-arepa", "corn-cake"],
    color: "bg-yellow-600",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Harina PAN 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 5200,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Mantequilla Alpina 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 6800,
        cantidadUso: 5,
        unidadUso: "g",
      },
    ],
    galeria: EXTRAS_GALERIA_BY_INDEX[3],
  }),
  extra({
    index: 4,
    nombre: "Limonada de Panela",
    marca: "Casa",
    costo: 0,
    precio: 1000,
    stock: 60,
    stockMinimo: 15,
    stockMaximo: 120,
    unidad: "vaso",
    ubicacionFisica: "Barra fría, bebidas",
    proveedor: "Producción propia",
    tiempoEntrega: "2 min",
    descripcionLarga:
      "Limonada natural endulzada con panela. Refrescante, ácida y dulce — la clásica de la casa.",
    especificaciones: [
      { clave: "Bebida", valor: "Limonada de panela" },
      { clave: "Precio", valor: "$1.000" },
    ],
    imageKeywords: ["lemonade", "panela-drink", "fresh-drink"],
    color: "bg-lime-500",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Limón Tahití 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 4500,
        cantidadUso: 60,
        unidadUso: "g",
      },
      {
        nombre: "Panela Pulverizada 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 3800,
        cantidadUso: 25,
        unidadUso: "g",
      },
    ],
    galeria: EXTRAS_GALERIA_BY_INDEX[4],
  }),
  extra({
    index: 5,
    nombre: "Fruta de Temporada",
    marca: "Casa",
    costo: 0,
    precio: 4000,
    stock: 45,
    stockMinimo: 12,
    stockMaximo: 90,
    unidad: "porción",
    ubicacionFisica: "Refrigerador, frutas",
    proveedor: "Verdulería local",
    tiempoEntrega: "3 min",
    descripcionLarga:
      "Porción generosa de fruta fresca del día. Papaya y melón cortados al momento — dulce, ligero y natural.",
    especificaciones: [
      { clave: "Frutas", valor: "Papaya y melón" },
      { clave: "Precio", valor: "$4.000" },
    ],
    imageKeywords: ["papaya", "melon", "fresh-fruit"],
    color: "bg-orange-400",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Papaya Maradol 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 3200,
        cantidadUso: 200,
        unidadUso: "g",
      },
      {
        nombre: "Melón Cantalupo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 4500,
        cantidadUso: 200,
        unidadUso: "g",
      },
    ],
    gruposModificadores: EXTRAS_MODIFIERS_BY_INDEX[5],
    galeria: EXTRAS_GALERIA_BY_INDEX[5],
  }),
  extra({
    index: 6,
    nombre: "Arroz con Leche",
    marca: "Casa",
    costo: 0,
    precio: 4000,
    stock: 40,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "porción",
    ubicacionFisica: "Refrigerador, postres",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Postre casero cremoso con canela y pasas. Servido frío en porción individual — ideal para cerrar con dulzura.",
    especificaciones: [
      { clave: "Postre", valor: "Arroz con leche" },
      { clave: "Precio", valor: "$4.000" },
    ],
    imageKeywords: ["rice-pudding", "arroz-con-leche", "dessert"],
    color: "bg-amber-100",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 60,
        unidadUso: "g",
      },
      {
        nombre: "Leche Entera Alquería 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 4200,
        cantidadUso: 250,
        unidadUso: "ml",
      },
      {
        nombre: "Pasas California 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 5200,
        cantidadUso: 15,
        unidadUso: "g",
      },
    ],
    galeria: EXTRAS_GALERIA_BY_INDEX[6],
  }),
  extra({
    index: 7,
    nombre: "Proteína Adicional",
    marca: "Casa",
    costo: 0,
    precio: 6000,
    stock: 50,
    stockMinimo: 12,
    stockMaximo: 100,
    unidad: "porción",
    ubicacionFisica: "Cocina, parrilla y plancha",
    proveedor: "Producción propia",
    tiempoEntrega: "12 min",
    descripcionLarga:
      "Porción extra de proteína para sumar a tu plato principal. Elige entre res, cerdo, pechuga, pierna, pernil dorado, milanesa o chuleta valluna — un solo ítem, varias opciones.",
    especificaciones: [
      { clave: "Precio", valor: "$6.000" },
      { clave: "Personalización", valor: "Elige proteína" },
    ],
    imageKeywords: ["grilled-meat", "chicken-milanese", "pork-chop"],
    color: "bg-red-700",
    shape: "square",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 180,
        unidadUso: "g",
      },
      {
        nombre: "Carne de Res en Bistec 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 22000,
        cantidadUso: 180,
        unidadUso: "g",
      },
    ],
    gruposModificadores: EXTRAS_MODIFIERS_BY_INDEX[7],
    galeria: EXTRAS_GALERIA_BY_INDEX[7],
  }),
  extra({
    index: 8,
    nombre: "Sopa Adicional",
    marca: "Casa",
    costo: 0,
    precio: 7000,
    stock: 35,
    stockMinimo: 8,
    stockMaximo: 70,
    unidad: "plato",
    ubicacionFisica: "Cocina, olla del día",
    proveedor: "Producción propia",
    tiempoEntrega: "10 min",
    descripcionLarga:
      "Sopa extra para acompañar tu almuerzo. Elige sancocho de pescado o ajiaco — porción abundante servida caliente.",
    especificaciones: [
      { clave: "Precio", valor: "$7.000" },
      { clave: "Opciones", valor: "Sancocho o ajiaco" },
    ],
    imageKeywords: ["fish-soup", "ajiaco", "colombian-soup"],
    color: "bg-emerald-700",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Filete de Tilapia 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 9800,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 120,
        unidadUso: "g",
      },
    ],
    gruposModificadores: EXTRAS_MODIFIERS_BY_INDEX[8],
    galeria: EXTRAS_GALERIA_BY_INDEX[8],
  }),
  extra({
    index: 9,
    nombre: "Adiciones al Pedido",
    marca: "Casa",
    costo: 0,
    precio: 0,
    stock: 999,
    stockMinimo: 0,
    stockMaximo: 999,
    unidad: "pedido",
    ubicacionFisica: "Cocina y barra — extras",
    proveedor: "Producción propia",
    tiempoEntrega: "Según ítems",
    descripcionLarga:
      "Sumar extras a cualquier plato: icópor, huevo frito, ensalada, bebidas y porciones de arroz o papas. Marca lo que necesites — el total se calcula al pedir.",
    especificaciones: [
      { clave: "Tipo", valor: "Extras opcionales" },
      { clave: "Uso", valor: "Complemento al plato principal" },
    ],
    imageKeywords: ["restaurant-extras", "side-dishes", "add-ons"],
    color: "bg-violet-600",
    shape: "square",
    gastoMargen: "estandar",
    destacado: true,
    isForSale: false,
    esReceta: false,
    gruposModificadores: EXTRAS_MODIFIERS_BY_INDEX[9],
    galeria: EXTRAS_GALERIA_BY_INDEX[9],
  }),
  extra({
    index: 10,
    nombre: "Principio Adicional",
    marca: "Casa",
    costo: 0,
    precio: 5000,
    stock: 60,
    stockMinimo: 15,
    stockMaximo: 120,
    unidad: "porción",
    ubicacionFisica: "Cocina, ollas del día",
    proveedor: "Producción propia",
    tiempoEntrega: "5 min",
    descripcionLarga:
      "Porción extra de principio para acompañar tu plato. Elige frijol, garbanzo o pasta — mismo precio, distinta opción.",
    especificaciones: [
      { clave: "Precio", valor: "$5.000" },
      { clave: "Opciones", valor: "Frijol, garbanzo o pasta" },
    ],
    imageKeywords: ["beans", "chickpeas", "pasta-side"],
    color: "bg-emerald-600",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Frijol Cargamanto 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 3200,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Garbanzo Seco 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 4500,
        cantidadUso: 150,
        unidadUso: "g",
      },
    ],
    gruposModificadores: EXTRAS_MODIFIERS_BY_INDEX[10],
    galeria: EXTRAS_GALERIA_BY_INDEX[10],
  }),
  extra({
    index: 11,
    nombre: "Coca-Cola",
    marca: "Coca-Cola",
    costo: 0,
    precio: 2500,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 150,
    unidad: "botella",
    ubicacionFisica: "Barra fría, bebidas",
    proveedor: "Postobón / Coca-Cola FEMSA",
    tiempoEntrega: "2 min",
    descripcionLarga:
      "Coca-Cola fría en presentación a elegir: 237 ml, 300 ml o 1.5 L. Perfecta para acompañar tu almuerzo.",
    especificaciones: [
      { clave: "Presentación", valor: "237 ml / 300 ml / 1.5 L" },
      { clave: "Precio base", valor: "$2.500 (237 ml)" },
    ],
    imageKeywords: ["coca-cola", "cola-bottle", "soda"],
    color: "bg-red-700",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: false,
    gruposModificadores: EXTRAS_MODIFIERS_BY_INDEX[11],
    galeria: EXTRAS_GALERIA_BY_INDEX[11],
  }),
  extra({
    index: 12,
    nombre: "Kola Román",
    marca: "Postobón",
    costo: 0,
    precio: 8000,
    stock: 40,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "botella",
    ubicacionFisica: "Barra fría, bebidas",
    proveedor: "Postobón",
    tiempoEntrega: "2 min",
    descripcionLarga:
      "Kola Román 1.5 L bien fría, la gaseosa costeña por excelencia. Ideal para compartir.",
    especificaciones: [
      { clave: "Presentación", valor: "Botella 1.5 L" },
      { clave: "Precio", valor: "$8.000" },
    ],
    imageKeywords: ["soda", "cola", "soft-drink"],
    color: "bg-amber-500",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[12],
  }),
  extra({
    index: 13,
    nombre: "Agua Bris 280ml",
    marca: "Postobón",
    costo: 0,
    precio: 1500,
    stock: 100,
    stockMinimo: 30,
    stockMaximo: 200,
    unidad: "botella",
    ubicacionFisica: "Barra fría, bebidas",
    proveedor: "Postobón",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Botella de agua Bris 280 ml bien fría. La opción ligera para hidratarte con la comida.",
    especificaciones: [
      { clave: "Presentación", valor: "Botella 280 ml" },
      { clave: "Precio", valor: "$1.500" },
    ],
    imageKeywords: ["bottled-water", "mineral-water", "bris"],
    color: "bg-sky-500",
    shape: "circle",
    gastoMargen: "conservador",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[13],
  }),
  extra({
    index: 14,
    nombre: "Huevo Adicional",
    marca: "Casa",
    costo: 0,
    precio: 1500,
    stock: 150,
    stockMinimo: 40,
    stockMaximo: 300,
    unidad: "unidad",
    ubicacionFisica: "Cocina, plancha",
    proveedor: "Producción propia",
    tiempoEntrega: "5 min",
    descripcionLarga:
      "Huevo adicional (perico, revuelto, frito o tortilla) para sumar a tu desayuno o almuerzo.",
    especificaciones: [
      { clave: "Precio", valor: "$1.500" },
      { clave: "Unidad", valor: "1 huevo" },
    ],
    imageKeywords: ["fried-egg", "egg", "breakfast"],
    color: "bg-yellow-500",
    shape: "circle",
    gastoMargen: "conservador",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[14],
  }),
  extra({
    index: 15,
    nombre: "Porción de Arroz",
    marca: "Casa",
    costo: 0,
    precio: 3000,
    stock: 120,
    stockMinimo: 30,
    stockMaximo: 240,
    unidad: "porción",
    ubicacionFisica: "Cocina, ollas del día",
    proveedor: "Producción propia",
    tiempoEntrega: "3 min",
    descripcionLarga:
      "Porción extra de arroz blanco para acompañar tu plato principal.",
    especificaciones: [
      { clave: "Precio", valor: "$3.000" },
      { clave: "Unidad", valor: "1 porción" },
    ],
    imageKeywords: ["white-rice", "rice-side", "colombian-rice"],
    color: "bg-emerald-500",
    shape: "square",
    gastoMargen: "conservador",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[15],
  }),
  extra({
    index: 16,
    nombre: "Entrada al Baño",
    marca: "Servicio",
    costo: 0,
    precio: 1000,
    stock: 500,
    stockMinimo: 50,
    stockMaximo: 1000,
    unidad: "uso",
    ubicacionFisica: "Servicios",
    proveedor: "Negocio",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Cargo por uso de las instalaciones sanitarias. Se cobra por persona.",
    especificaciones: [
      { clave: "Precio", valor: "$1.000 por persona" },
      { clave: "Tipo", valor: "Servicio" },
    ],
    imageKeywords: ["service-fee", "bathroom", "access"],
    color: "bg-slate-600",
    shape: "square",
    gastoMargen: "conservador",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[16],
  }),
  extra({
    index: 17,
    nombre: "Agua Bris 600ml",
    marca: "Postobón",
    costo: 0,
    precio: 2000,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 160,
    unidad: "botella",
    ubicacionFisica: "Barra fría, bebidas",
    proveedor: "Postobón",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Botella de agua Bris 600 ml bien fría. La opción mediana para hidratarte con la comida.",
    especificaciones: [
      { clave: "Presentación", valor: "Botella 600 ml" },
      { clave: "Precio", valor: "$2.000" },
    ],
    imageKeywords: ["bottled-water", "mineral-water", "bris"],
    color: "bg-cyan-500",
    shape: "circle",
    gastoMargen: "conservador",
    esReceta: false,
    galeria: EXTRAS_GALERIA_BY_INDEX[17],
  }),
];
